import { Circle, CircleCheck, CircleX } from "lucide-react";
import { formatRelative } from "@/lib/utils";
import type { MdfRequestStatus, MdfStatusHistoryEntry } from "./types";

// Read-only vertical timeline derived from mdf_requests.status_history (a
// JSONB column populated by triggers on status transitions). Entries flow
// chronologically: draft → pending → approved/declined → spent/cancelled.
// Partner-facing — no actor names rendered.

type Props = {
  history: MdfStatusHistoryEntry[] | null;
  currentStatus: MdfRequestStatus;
  createdAt: string;
  approvedAt?: string | null;
  declinedReason?: string | null;
  spentAt?: string | null;
};

type TimelineEvent = {
  label: string;
  timestamp: string;
  tone: "neutral" | "success" | "failure";
};

export function MdfDecisionTimeline({
  history,
  currentStatus,
  createdAt,
  approvedAt,
  declinedReason,
  spentAt,
}: Props) {
  const events = buildEvents({
    history,
    currentStatus,
    createdAt,
    approvedAt,
    spentAt,
  });

  if (events.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-foreground">Activity</h3>
      <ol className="mt-3 space-y-3">
        {events.map((e, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center"
            >
              {e.tone === "success" ? (
                <CircleCheck className="h-4 w-4 text-foreground" />
              ) : e.tone === "failure" ? (
                <CircleX className="h-4 w-4 text-destructive" />
              ) : (
                <Circle className="h-2.5 w-2.5 fill-muted-foreground text-muted-foreground" />
              )}
            </span>
            <div className="min-w-0">
              <p className="text-sm text-foreground">{e.label}</p>
              <p className="text-xs text-muted-foreground">
                {formatRelative(e.timestamp)}
              </p>
            </div>
          </li>
        ))}
      </ol>
      {declinedReason ? (
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3">
          <p className="text-xs font-medium uppercase tracking-wider text-destructive">
            Reason
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm italic text-foreground">
            {declinedReason}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function buildEvents(args: {
  history: MdfStatusHistoryEntry[] | null;
  currentStatus: MdfRequestStatus;
  createdAt: string;
  approvedAt?: string | null;
  spentAt?: string | null;
}): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Always start with creation. status_history may or may not include the
  // initial transition; render an explicit "Submitted" row so the timeline
  // reads cleanly.
  events.push({
    label: args.currentStatus === "draft" ? "Draft created" : "Submitted",
    timestamp: args.createdAt,
    tone: "neutral",
  });

  // Walk recorded transitions if present.
  const seen = new Set<string>();
  for (const entry of args.history ?? []) {
    const to = (entry.to ?? entry.status ?? "").toLowerCase();
    if (!to || to === "draft" || to === "pending") continue;
    const dedupeKey = `${to}|${entry.changed_at}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    events.push(eventForTransition(to, entry.changed_at));
  }

  // Backfill from explicit decision timestamps when status_history is
  // sparse (legacy rows may pre-date the trigger).
  if (
    args.approvedAt &&
    !events.some((e) => e.label.toLowerCase().includes("approved"))
  ) {
    events.push({
      label: "Approved",
      timestamp: args.approvedAt,
      tone: "success",
    });
  }
  if (
    args.spentAt &&
    !events.some((e) => e.label.toLowerCase().includes("spent"))
  ) {
    events.push({
      label: "Marked spent",
      timestamp: args.spentAt,
      tone: "neutral",
    });
  }

  // Sort ascending by timestamp; "Submitted" stays at the top since
  // created_at is the earliest.
  events.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  return events;
}

function eventForTransition(to: string, at: string): TimelineEvent {
  switch (to) {
    case "approved":
      return { label: "Approved", timestamp: at, tone: "success" };
    case "declined":
      return { label: "Declined", timestamp: at, tone: "failure" };
    case "spent":
      return { label: "Marked spent", timestamp: at, tone: "neutral" };
    case "cancelled":
      return { label: "Cancelled", timestamp: at, tone: "neutral" };
    default:
      return { label: to, timestamp: at, tone: "neutral" };
  }
}
